import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { TEST_IDS } from "@/lib/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Renders the newsletter signup. Variants:
// - "section": full editorial signup form
// - "inline":  compact single-line variant used inside the footer
// - "strip":   ultra-discreet horizontal strip used mid-page
// Derive a clean source label from the visitor's current pathname.
// e.g. "/"            -> "home"
//      "/how-it-works"-> "how_it_works"
//      "/resources/why-traditional-banking-fails" -> "resources_why_traditional_banking_fails"
const pageSourceFromPath = (variant) => {
  if (typeof window === "undefined") return "unknown";
  const path = window.location.pathname || "/";
  let slug = path.replace(/^\/+|\/+$/g, "");
  if (!slug) slug = "home";
  slug = slug.replace(/[/-]+/g, "_").toLowerCase();
  // Distinguish footer-vs-strip-vs-section submissions on the same page
  const v = variant === "inline" ? "footer" : variant;
  return `${slug}__${v}`;
};

export const NewsletterForm = ({ variant = "section", source }) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Explicit source prop wins (back-compat for any caller that passes one).
  // Otherwise auto-fill from the visitor's current page.
  const sourceLabel = source || pageSourceFromPath(variant);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please add your first name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, {
        first_name: firstName.trim(),
        email: email.trim(),
        source: sourceLabel,
      });
      setDone(true);
      toast.success("Subscribed. Welcome to the letter.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your email.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={TEST_IDS.newsletter.success}
        className={
          variant === "section"
            ? "bg-paper border border-camel/30 p-8 md:p-10 text-center"
            : "bg-paper border border-camel/30 p-6 text-center"
        }
      >
        <CheckCircle2 className="w-6 h-6 mx-auto text-camel" strokeWidth={1.25} />
        <p className="mt-4 font-serif italic text-nearblack text-xl md:text-2xl">
          Welcome, {firstName.split(" ")[0]}.
        </p>
        <p className="mt-2 text-[13px] text-nearblack/65 leading-relaxed font-light">
          The next letter is on its way to{" "}
          <span className="text-nearblack">{email}</span>.
        </p>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <form
        onSubmit={handleSubmit}
        noValidate
        data-testid={TEST_IDS.newsletter.form}
        className="grid grid-cols-1 sm:grid-cols-7 gap-3 bg-paper border border-paperdeep p-5"
      >
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          data-testid={TEST_IDS.newsletter.firstName}
          placeholder="First name"
          disabled={loading}
          className="sm:col-span-3 bg-transparent border-b border-paperdeep py-2 px-1 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel text-sm"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid={TEST_IDS.newsletter.email}
          placeholder="you@email.com"
          disabled={loading}
          className="sm:col-span-3 bg-transparent border-b border-paperdeep py-2 px-1 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel text-sm"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid={TEST_IDS.newsletter.submit}
          className="sm:col-span-1 btn-camel inline-flex items-center justify-center disabled:opacity-60"
        >
          {loading ? "…" : "Join"}
        </button>
      </form>
    );
  }

  if (variant === "strip") {
    return (
      <form
        onSubmit={handleSubmit}
        noValidate
        data-testid={TEST_IDS.newsletter.form}
        className="flex flex-col sm:flex-row gap-3"
      >
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          data-testid={TEST_IDS.newsletter.firstName}
          placeholder="First name"
          disabled={loading}
          className="flex-1 bg-paper border border-paperdeep py-3 px-4 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel text-sm"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid={TEST_IDS.newsletter.email}
          placeholder="you@email.com"
          disabled={loading}
          className="flex-1 bg-paper border border-paperdeep py-3 px-4 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel text-sm"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid={TEST_IDS.newsletter.submit}
          className="btn-camel inline-flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap"
        >
          {loading ? "Sending..." : "Join the Letter"}
          {!loading && <ArrowRight className="w-3.5 h-3.5" />}
        </button>
      </form>
    );
  }

  // section variant (default)
  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid={TEST_IDS.newsletter.form}
      className="bg-paper border border-paperdeep p-8 md:p-10"
    >
      <label className="block label-small text-stonegrey mb-2" style={{ fontSize: "0.65rem" }}>
        First Name
      </label>
      <input
        type="text"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        data-testid={TEST_IDS.newsletter.firstName}
        placeholder="Your first name"
        disabled={loading}
        className="w-full border-b border-paperdeep bg-transparent py-3 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors"
      />
      <label className="block label-small text-stonegrey mt-7 mb-2" style={{ fontSize: "0.65rem" }}>
        Email
      </label>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid={TEST_IDS.newsletter.email}
        placeholder="you@email.com"
        disabled={loading}
        className="w-full border-b border-paperdeep bg-transparent py-3 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid={TEST_IDS.newsletter.submit}
        className="mt-9 w-full btn-camel inline-flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {loading ? "Sending..." : "Join the Letter"}
        {!loading && <ArrowRight className="w-3.5 h-3.5" />}
      </button>
      <p className="mt-5 text-[11px] text-stonegrey leading-relaxed">
        One thoughtful letter at a time. Unsubscribe anytime.
      </p>
    </form>
  );
};

export const Newsletter = () => {
  return (
    <section
      id="newsletter"
      data-testid={TEST_IDS.newsletter.section}
      className="bg-paper py-28 md:py-36 border-y border-paperdeep"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-7">The Letter</p>
            <h2
              className="font-serif text-nearblack leading-[1.06] text-balance"
              style={{ fontSize: "clamp(2rem, 4vw, 3.6rem)", fontWeight: 400 }}
            >
              A weekly letter on building
              <br className="hidden md:block" />
              <em className="italic text-camel">
                financial systems with intention
              </em>
              .
            </h2>
            <p className="mt-8 text-nearblack/75 text-base md:text-lg leading-[1.85] font-light max-w-lg">
              Short, thoughtful notes on financial literacy, cash flow,
              Infinite Banking, money mindset, books worth reading, and the
              quiet work of building wealth over a lifetime.
            </p>

            <ul className="mt-10 space-y-3">
              {[
                "Financial literacy & wealth building",
                "Infinite Banking concepts & practice",
                "Cash flow strategies",
                "Books, references, and resources",
                "Money mindset & long-term thinking",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-4 text-[14px] text-nearblack/75 font-light"
                >
                  <span className="block w-2 h-px bg-camel" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <NewsletterForm variant="section" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Compact mid-page CTA strip — sits between educational sections
export const NewsletterStrip = () => {
  return (
    <section
      data-testid="newsletter-strip-section"
      className="bg-nearblack text-paper py-16 md:py-20"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow-light mb-4">The Letter</p>
            <h3
              className="font-serif text-paper leading-[1.1] text-balance"
              style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.25rem)", fontWeight: 400 }}
            >
              Quiet, weekly notes on{" "}
              <em className="italic text-camellight">
                financial systems and long-term thinking
              </em>
              .
            </h3>
          </div>
          <div className="lg:col-span-6">
            <NewsletterForm variant="strip" />
          </div>
        </div>
      </div>
    </section>
  );
};
